/*
 * Routine builder (sections/liore-routine-builder.liquid).
 *
 * The top half is pure state logic with no DOM access, so it runs under
 * node:test (tests/routine/). The custom element below draws the builder from
 * that state and is only defined in the browser.
 *
 * State is plain data and every change returns a new object; the element
 * re-renders from it and saves it to localStorage so a returning visitor
 * finds their routine as they left it.
 */

const STATE_VERSION = 1;

/** @typedef {{ id: number, variantId: number, title: string, price: number, compareAt?: number }} Product */
/** @typedef {{ id: string, starter: boolean, productId: number | null, sizeIds?: number[] }} StepData */
/** @typedef {{ id: string, inRoutine: boolean, productId: number | null, skipped: boolean }} StepState */
/** @typedef {{ version: number, skin: string | null, steps: StepState[], picked: Record<string, Product> }} State */

/**
 * A fresh routine: the starter pack, or the starter steps left empty.
 * @param {{ steps: StepData[] }} data
 * @param {'starter' | 'blank'} mode
 * @param {string | null} [skin]
 * @returns {State}
 */
export function createState(data, mode, skin = null) {
  return {
    version: STATE_VERSION,
    skin,
    picked: {},
    steps: data.steps.map((step) => ({
      id: step.id,
      inRoutine: Boolean(step.starter),
      productId: mode === 'starter' ? starterProduct(step, data.products || {}) : null,
      skipped: false,
    })),
  };
}

/**
 * The product a starter step begins with: the smallest of its sizes that is
 * for sale (unpublished sizes are not in the data at all). Steps without
 * readable sizes keep the product set in the theme editor.
 * @param {StepData} step @param {Record<string, Product>} products
 */
function starterProduct(step, products) {
  const amount = (id) => parseSize(products[id].title)?.amount ?? Infinity;
  const forSale = [step.productId, ...(step.sizeIds || [])].filter(
    (id) => id != null && products[id] && products[id].available !== false,
  );
  if (forSale.length === 0) return step.productId ?? null;
  return forSale.reduce((best, id) => (amount(id) < amount(best) ? id : best));
}

/** @param {State} state @param {string} id @param {(step: StepState) => StepState} change */
function updateStep(state, id, change) {
  return { ...state, steps: state.steps.map((step) => (step.id === id ? change(step) : step)) };
}

/** @param {State} state @param {string | null} skin */
export function setSkin(state, skin) {
  return { ...state, skin };
}

/** @param {State} state @param {string} stepId */
export function toggleSkip(state, stepId) {
  return updateStep(state, stepId, (step) => ({ ...step, skipped: !step.skipped }));
}

/**
 * Puts a product in a step. Products loaded while browsing a step are not in
 * the page data, so they are kept in `picked` for the summary and for restore.
 * @param {State} state @param {string} stepId @param {Product} product
 */
export function chooseProduct(state, stepId, product) {
  const next = updateStep(state, stepId, (step) => ({ ...step, productId: product.id, skipped: false }));
  return { ...next, picked: { ...state.picked, [product.id]: product } };
}

/** @param {State} state @param {string} stepId */
export function addStep(state, stepId) {
  return updateStep(state, stepId, (step) => ({ ...step, inRoutine: true, skipped: false }));
}

/**
 * Takes an added step out again. Starter steps stay; they are skipped instead,
 * so the routine keeps its order and numbering.
 * @param {State} state @param {string} stepId @param {{ steps: StepData[] }} data
 */
export function removeStep(state, stepId, data) {
  if (data.steps.find((step) => step.id === stepId)?.starter) return state;
  return updateStep(state, stepId, (step) => ({ ...step, inRoutine: false, skipped: false }));
}

/** @param {State} state @param {{ products: Record<string, Product> }} data @param {number | null} id */
export function findProduct(state, data, id) {
  if (id == null) return null;
  return data.products[id] || state.picked[id] || null;
}

/**
 * The routine as the summary and the cart see it.
 * @param {State} state @param {{ products: Record<string, Product> }} data
 */
export function summarize(state, data) {
  const lines = state.steps
    .filter((step) => step.inRoutine)
    .map((step) => {
      const product = findProduct(state, data, step.productId);
      let status = 'chosen';
      if (step.skipped) status = 'skipped';
      else if (!product) status = 'empty';
      // Sold out: shown so the visitor can swap it, but /cart/add.js would
      // reject the whole routine if it were sent.
      else if (product.available === false) status = 'unavailable';
      return { stepId: step.id, product, status, priceCents: status === 'chosen' ? product.price : 0 };
    });
  const chosen = lines.filter((line) => line.status === 'chosen');
  return {
    lines,
    count: chosen.length,
    totalCents: chosen.reduce((sum, line) => sum + line.priceCents, 0),
    variantIds: chosen.map((line) => line.product.variantId),
  };
}

/**
 * What choosing a product for a step would do, for the product panel's
 * footer: the product it replaces (if any) and the routine total with it.
 * @param {State} state @param {{ products: Record<string, Product> }} data @param {string} stepId @param {Product} product
 */
export function previewChoice(state, data, stepId, product) {
  const step = state.steps.find((entry) => entry.id === stepId);
  const current = step && step.inRoutine && !step.skipped ? findProduct(state, data, step.productId) : null;
  const alreadyChosen = current?.id === product.id;
  const next = chooseProduct(addStep(state, stepId), stepId, product);
  return {
    replaced: alreadyChosen ? null : current,
    alreadyChosen,
    totalCents: summarize(next, data).totalCents,
  };
}

/* ---- Share links -------------------------------------------------------
 * A routine fits in the part of the address after "#":
 *   dry.boj0004.tir0004.-.skn0113._
 * skin type (its collection handle without "-skin"; "_" for none), then one
 * token per step in the order of the page's steps: a product code, "-" for
 * a skipped step, "_" for a step in the routine without a product, and
 * nothing for an extra step that is not in the routine (trailing ones are
 * dropped). Product codes are the SKU when it is link-safe, else the barcode.
 */

const CODE_PATTERN = /^[a-z0-9-]+$/;

/** @param {{ sku?: string, barcode?: string }} product */
export function linkCode(product) {
  const sku = String(product?.sku ?? '').trim();
  if (/^[A-Za-z0-9-]+$/.test(sku)) return sku.toLowerCase();
  const barcode = String(product?.barcode ?? '').trim();
  if (/^\d+$/.test(barcode)) return barcode;
  return null;
}

const skinSlug = (handle) => String(handle).replace(/-skin$/, '');

/**
 * @param {State} state @param {{ steps: StepData[] }} data
 * @param {(id: number) => any} lookup product by id
 */
export function encodeRoutine(state, data, lookup) {
  const skin = state.skin ? skinSlug(state.skin) : '_';
  const tokens = data.steps.map((stepData) => {
    const step = state.steps.find((s) => s.id === stepData.id);
    if (!step || !step.inRoutine) return '';
    if (step.skipped) return '-';
    const product = step.productId != null ? lookup(step.productId) : null;
    return (product && linkCode(product)) || '_';
  });
  while (tokens.length && tokens[tokens.length - 1] === '') tokens.pop();
  return [skin, ...tokens].join('.');
}

/**
 * @param {string} hash the part after "#" @param {{ steps: StepData[], skins?: { handle: string }[] }} data
 * @returns {{ skin: string | null, steps: { id: string, inRoutine: boolean, skipped: boolean, code: string | null }[] } | null}
 */
export function decodeRoutine(hash, data) {
  const parts = String(hash || '').replace(/^#/, '').toLowerCase().split('.');
  if (parts.length < 2) return null;
  const [skinPart, ...tokens] = parts;
  const skin = (data.skins || []).find((s) => skinSlug(s.handle) === skinPart)?.handle ?? null;
  const steps = data.steps.map((stepData, index) => {
    const token = tokens[index] ?? '';
    if (token === '' && !stepData.starter) return { id: stepData.id, inRoutine: false, skipped: false, code: null };
    if (token === '-') return { id: stepData.id, inRoutine: true, skipped: true, code: null };
    const code = token !== '_' && CODE_PATTERN.test(token) ? token : null;
    return { id: stepData.id, inRoutine: true, skipped: false, code };
  });
  return { skin, steps };
}

/** A product's handle, from its URL; for statistics, which never need more. */
function handleOf(product) {
  return product?.url ? product.url.split('?')[0].split('/').filter(Boolean).pop() : '';
}

/**
 * Cart lines for /cart/add.js. The same product in two steps becomes one line
 * with a higher quantity. A source ("routine-builder", "routine-shared") goes
 * on each line as a hidden `_source` property, so orders show which lines came
 * from the builder; the theme never displays properties starting with "_".
 * @param {number[]} variantIds @param {string} [source]
 */
export function cartItems(variantIds, source) {
  const quantities = new Map();
  for (const id of variantIds) quantities.set(id, (quantities.get(id) || 0) + 1);
  return [...quantities].map(([id, quantity]) => (source ? { id, quantity, properties: { _source: source } } : { id, quantity }));
}

/**
 * Message after adding the routine to the cart, from per-product results:
 * how many went in, and which did not and why (Shopify's own words, e.g.
 * "the maximum quantity is already in your cart").
 * @param {{ ok: boolean, quantity: number, title: string, reason?: string }[]} results
 * @param {{ added: string, addedOne?: string, notAdded: string }} labels
 */
export function cartReport(results, labels) {
  const added = results.filter((r) => r.ok).reduce((sum, r) => sum + r.quantity, 0);
  const failed = results.filter((r) => !r.ok);
  return {
    added,
    status: added === 1 && labels.addedOne ? labels.addedOne : added ? fill(labels.added, { count: added }) : '',
    error: failed.length ? `${labels.notAdded} ${failed.map((r) => (r.reason ? `${r.title}: ${r.reason}` : r.title)).join('; ')}` : '',
  };
}

/**
 * Order of the swap row: what is in the routine now, then products for the
 * visitor's skin type, then everything else in the step's collection.
 * Strong actives are always offered; the cards tag them instead.
 */
export function orderAlternatives({ currentId, skinIds, loadedIds }) {
  const seen = new Set();
  const ordered = [];
  for (const id of [currentId, ...skinIds, ...loadedIds]) {
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

/**
 * Where an arrow click takes a swap row: as many whole cards as fit in view,
 * landing on a card edge. Within half a card of either end it goes all the
 * way, so the arrows never leave a sliver to scroll.
 * @param {{ current: number, max: number, step: number, gap?: number, view: number, direction: 'back' | 'forward' }} row
 *   step is the distance between two card starts (card width + gap)
 */
export function nextScrollLeft({ current, max, step, gap = 0, view, direction }) {
  const back = direction === 'back';
  if (!(step > 0)) return back ? 0 : max;
  const perView = Math.max(1, Math.floor((view + gap) / step));
  let target = (Math.round(current / step) + (back ? -perView : perView)) * step;
  if (!back && max - target < step / 2) target = max;
  if (back && target < step / 2) target = 0;
  return Math.min(max, Math.max(0, target));
}

/**
 * Size shown on a size choice, read from the product title: a piece count
 * ("1pc", "4ea") wins over a volume or weight ("34g"), because masks list both.
 * The piece words come from the shop's texts ("[count] tk" in Estonian);
 * ml and g read the same in every language.
 * @param {string} title @param {{ piece?: string, pieces?: string }} [units]
 */
export function sizeLabel(title, units = {}) {
  const size = parseSize(title);
  if (!size) return '';
  if (size.unit === 'pc') {
    const one = size.amount === 1;
    const template = (one ? units.piece : units.pieces) || (one ? '[count] pc' : '[count] pcs');
    return template.replace('[count]', size.text);
  }
  return `${size.text} ${size.unit}`;
}

/**
 * The size a product title names: a piece count ("1pc", "4ea") wins over a
 * volume or weight ("34g"); otherwise the last ml or g amount.
 * @param {string} title
 * @returns {{ amount: number, text: string, unit: 'pc' | 'ml' | 'g' } | null}
 */
function parseSize(title) {
  const count = /(\d+)\s*(pcs?|ea|tk)\b/i.exec(title);
  if (count) return { amount: Number(count[1]), text: count[1], unit: 'pc' };
  const last = [...title.matchAll(/(\d+(?:[.,]\d+)?)\s*(ml|g)\b/gi)].at(-1);
  if (!last) return null;
  return { amount: Number(last[1].replace(',', '.')), text: last[1], unit: /** @type {'ml' | 'g'} */ (last[2].toLowerCase()) };
}

/**
 * Whether a step has anything to choose from: its collection, or the
 * hand-picked "Products to choose from" list set in the theme editor.
 * @param {{ collection?: object | null, choiceIds?: number[] }} step
 */
export function canChoose(step) {
  return Boolean(step.collection) || (step.choiceIds?.length ?? 0) > 0;
}

/**
 * Size choices for a step: its suggested product plus the other sizes set in
 * the theme editor. Only offered while the step holds one of those sizes; an
 * empty step or a swapped-in product has nothing to resize.
 * @param {StepData} step @param {number | null} productId @param {(id: number) => any} lookup
 */
export function sizeOptions(step, productId, lookup, units) {
  const ids = [...new Set([step.productId, ...(step.sizeIds || [])].filter((id) => id != null))];
  if (productId == null || !ids.includes(productId)) return [];
  // Smallest first; a title without a readable size goes last.
  const amount = (product) => parseSize(product.title)?.amount ?? Infinity;
  const options = ids
    .map((id) => lookup(id))
    .filter(Boolean)
    .sort((a, b) => amount(a) - amount(b))
    .map((product) => ({ id: product.id, label: sizeLabel(product.title, units) || product.title, available: product.available !== false }));
  return options.length > 1 ? options : [];
}

/** @param {number} cents @param {number} decimals @param {string} thousands @param {string} decimal */
function formatAmount(cents, decimals, thousands, decimal) {
  const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
}

/**
 * Shopify's money format (shop.money_format), e.g. "€{{amount_with_comma_separator}}".
 * @param {number} cents @param {string} format
 */
export function formatMoney(cents, format) {
  const amounts = {
    amount: () => formatAmount(cents, 2, ',', '.'),
    amount_no_decimals: () => formatAmount(cents, 0, ',', '.'),
    amount_with_comma_separator: () => formatAmount(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => formatAmount(cents, 0, '.', ','),
    amount_with_apostrophe_separator: () => formatAmount(cents, 2, "'", '.'),
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => (amounts[key] ? amounts[key]() : match));
}

/**
 * Rebuilds a saved routine against today's page data. Steps that no longer
 * exist are dropped, new steps take their starter defaults, and a product the
 * page can no longer describe empties its step. Returns null when the saved
 * value is unusable, so the caller falls back to the starter pack.
 * @param {unknown} saved @param {{ steps: StepData[], products: Record<string, Product> }} data
 * @returns {State | null}
 */
export function restoreState(saved, data) {
  if (!saved || typeof saved !== 'object') return null;
  const record = /** @type {Record<string, any>} */ (saved);
  if (record.version !== STATE_VERSION || !Array.isArray(record.steps)) return null;

  const picked = record.picked && typeof record.picked === 'object' ? record.picked : {};
  const fresh = createState(data, 'starter', typeof record.skin === 'string' ? record.skin : null);
  const keptPicked = {};

  const steps = fresh.steps.map((step) => {
    const old = record.steps.find((entry) => entry && entry.id === step.id);
    if (!old) return step;
    const starter = data.steps.find((entry) => entry.id === step.id)?.starter;
    let productId = Number.isInteger(old.productId) ? old.productId : null;
    if (productId != null && !data.products[productId]) {
      const known = picked[productId];
      if (known && Number.isInteger(known.variantId) && Number.isInteger(known.price)) keptPicked[productId] = known;
      else productId = null;
    }
    return { id: step.id, inRoutine: starter ? true : Boolean(old.inRoutine), productId, skipped: Boolean(old.skipped) };
  });

  return { ...fresh, steps, picked: keptPicked };
}

/* ------------------------------------------------------------------------ */
/* Browser element                                                          */
/* ------------------------------------------------------------------------ */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const fill = (template, values) =>
  String(template ?? '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

if (typeof customElements !== 'undefined' && !customElements.get('liore-routine-builder')) {
  class LioreRoutineBuilder extends HTMLElement {
    /** @type {any} */ data;
    /** @type {State} */ state;
    /** @type {string | null} */ openStepId = null;
    showSkinPicker = false;
    /** step id -> { ids: number[], page: number, done: boolean, loading: boolean } */
    loaded = new Map();
    observer = null;

    connectedCallback() {
      const source = this.querySelector('script[type="application/json"][data-routine-data]');
      if (!source) return;
      this.data = JSON.parse(source.textContent || '{}');
      this.data.extra = {};
      this.labels = this.data.labels || {};
      // Which of a skin type's products belong to each step, from the step
      // collections each product is in (computed once here, not in Liquid).
      for (const skin of this.data.skins) {
        skin.steps = {};
        for (const step of this.data.steps) {
          const handle = step.collection?.handle;
          if (handle) skin.steps[step.id] = skin.productIds.filter((id) => this.data.memberships[id]?.includes(handle));
          else skin.steps[step.id] = skin.productIds.filter((id) => step.choiceIds?.includes(id));
        }
      }
      this.state = this.#restore() || createState(this.data, 'starter');
      // A shared link wins over what this browser had saved.
      const shared = location.hash.length > 1 ? decodeRoutine(location.hash.slice(1), this.data) : null;
      if (shared) this.#openShared(shared);
      this.addEventListener('click', this.#onClick);
      this.dialog = this.querySelector('[data-product-dialog]');
      this.dialog?.addEventListener('close', this.#onPanelClose);
      // A click on the backdrop lands on the <dialog> itself.
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.render();
      this.#watchSummary();
    }

    /**
     * The phone's bottom bar repeats the summary's total and cart button; it
     * steps aside while the summary card itself is on screen, so the button
     * never shows twice.
     */
    #watchSummary() {
      const summary = this.querySelector('.lrb-summary');
      const bar = this.querySelector('.lrb-bar');
      if (!summary || !bar || typeof IntersectionObserver === 'undefined') return;
      // Shown only while the summary is still below the screen: at the card
      // (same button) or past it (the bar would ride up with the page end and
      // flash at the top) it stays away.
      this.summaryObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        const below = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        bar.classList.toggle('lrb-bar--away', !below);
      });
      this.summaryObserver.observe(summary);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.observer?.disconnect();
      this.arrowObserver?.disconnect();
      this.summaryObserver?.disconnect();
    }

    #restore() {
      try {
        const raw = localStorage.getItem(this.data.storageKey);
        return raw ? restoreState(JSON.parse(raw), this.data) : null;
      } catch {
        return null;
      }
    }

    #save() {
      try {
        localStorage.setItem(this.data.storageKey, JSON.stringify(this.state));
      } catch {
        /* private mode or storage full: the routine still works for this visit */
      }
    }

    /** @param {State} next */
    #set(next) {
      this.state = next;
      this.#save();
      this.render();
    }

    /**
     * Brings a just-added step into view: a smooth scroll to the top of its
     * row (CSS scroll-margin keeps it clear of the sticky header) and a short
     * highlight. Visitors who prefer reduced motion get an instant jump and no
     * highlight. Focus moves to the step's own button, without a second scroll.
     */
    #revealStep(stepId, { onlyIfNeeded = false } = {}) {
      const row = this.querySelector(`[data-step-row="${CSS.escape(stepId)}"]`);
      if (!row) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      row.querySelector('.lrb-step__actions button')?.focus({ preventScroll: true });
      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const box = row.getBoundingClientRect();
      const visible = box.top >= header && box.bottom <= window.innerHeight;
      if (!onlyIfNeeded || !visible) row.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      if (reduce) return;
      row.classList.add('lrb-step--new');
      row.addEventListener('animationend', () => row.classList.remove('lrb-step--new'), { once: true });
    }

    /* ---------------- share links ---------------- */

    /** Every product the page knows, by its link code (SKU or barcode). */
    #codeIndex() {
      const index = new Map();
      for (const product of Object.values({ ...this.state.picked, ...this.data.extra, ...this.data.products })) {
        const code = linkCode(product);
        if (code) index.set(code, product);
        if (/^\d+$/.test(String(product.barcode || ''))) index.set(String(product.barcode), product);
      }
      return index;
    }

    /** Builds the routine from a shared link, looking up products the page did not load. */
    #openShared(shared) {
      this.fromShare = true;
      this.#track('routine_shared_opened');
      const index = this.#codeIndex();
      let state = createState(this.data, 'blank', shared.skin);
      const missing = [];
      for (const step of shared.steps) {
        if (step.inRoutine) state = addStep(state, step.id);
        if (step.skipped) state = toggleSkip(state, step.id);
        if (!step.code) continue;
        const known = index.get(step.code);
        if (known) state = chooseProduct(state, step.id, known);
        else missing.push(step);
      }
      // Starter steps the link leaves out stay in the routine but empty.
      this.state = state;
      this.#save();
      // Drop the routine from the address so later changes are not overwritten on reload.
      history.replaceState(history.state, '', location.pathname + location.search);
      const notice = this.querySelector('[data-notice]');
      if (notice) {
        notice.textContent = this.labels.shareLoaded;
        notice.hidden = false;
      }
      if (missing.length) this.#resolveShared(missing);
    }

    async #resolveShared(steps) {
      for (const step of steps) {
        try {
          const suggest = await fetch(`${this.data.searchUrl}.json?q=${encodeURIComponent(step.code)}&resources[type]=product&resources[limit]=1`).then((r) => r.json());
          const hit = suggest?.resources?.results?.products?.[0];
          if (!hit) continue;
          const sections = await fetch(`${hit.url.split('?')[0]}?sections=liore-routine-item`).then((r) => r.json());
          const html = sections['liore-routine-item'] || '';
          const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-routine-item]')?.textContent;
          if (!json) continue;
          const product = JSON.parse(json);
          this.data.extra[product.id] = product;
          const current = this.state.steps.find((s) => s.id === step.id);
          // Fill the step only if the visitor has not changed it meanwhile.
          if (current && current.productId == null && !current.skipped) this.#set(chooseProduct(this.state, step.id, product));
        } catch {
          /* unknown or unavailable product: the step stays empty to choose again */
        }
      }
    }

    async #share(button) {
      const L = this.labels;
      const hash = encodeRoutine(this.state, this.data, (id) => this.product(id));
      const base = this.data.shareUrl || location.origin + location.pathname + location.search;
      const url = `${base}#${hash}`;
      const status = this.querySelector('[data-share-status]');
      const label = button?.querySelector('[data-share-label]');
      // The button itself confirms the copy (green, ✓, "Link copied"), then
      // returns to normal; the hidden status line tells screen readers.
      const say = (text) => {
        if (status) status.textContent = text;
        if (button && label) {
          button.classList.add('is-copied');
          label.textContent = text;
        }
        clearTimeout(this.shareTimer);
        this.shareTimer = setTimeout(() => {
          if (status) status.textContent = '';
          if (button && label) {
            button.classList.remove('is-copied');
            label.textContent = L.share;
          }
        }, 3000);
      };
      // Phones: the system share sheet (Messages, WhatsApp, mail…). Elsewhere: copy.
      const touch = window.matchMedia?.('(pointer: coarse)').matches;
      if (touch && navigator.share) {
        try {
          await navigator.share({ title: L.shareTitle, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        say(L.shareCopied);
      } catch {
        // No clipboard access: show the link so it can be copied by hand.
        window.prompt(L.shareTitle, url);
      }
      button?.focus();
    }

    /* ---------------- product panel ---------------- */

    /** product id -> HTML of the panel body (pictures, price, rating, details) */
    panelCache = new Map();

    #openPanel(stepId, productId, trigger) {
      const product = this.product(productId);
      if (!this.dialog || !product) return;
      this.panelState = { stepId, id: productId, trigger };
      // Names the dialog until the product's own title (aria-labelledby) has loaded.
      this.dialog.setAttribute('aria-label', product.title);
      this.#fillPanelFrame();
      const body = this.dialog.querySelector('[data-dialog-body]');
      body.innerHTML = `<p class="lrb-dialog__status" role="status">${escapeHtml(this.labels.panelLoading)}</p>`;
      this.dialog.showModal();
      body.scrollTop = 0;
      this.#loadPanel(product);
    }

    /** Step name, what the product replaces, the new total and the buttons. */
    #fillPanelFrame() {
      const L = this.labels;
      const { stepId, id } = this.panelState;
      const product = this.product(id);
      const data = this.stepData(stepId);
      const inRoutine = this.state.steps.filter((s) => s.inRoutine);
      let number = inRoutine.findIndex((s) => s.id === stepId) + 1;
      if (number === 0) number = inRoutine.length + 1;

      const stepText = fill(L.panelFor, { number: String(number).padStart(2, '0'), step: data.name });
      this.dialog.querySelectorAll('[data-dialog-step]').forEach((el) => (el.textContent = stepText));

      const preview = previewChoice(this.state, this.allProducts(), stepId, product);
      const parts = [];
      if (preview.replaced) {
        parts.push(`<span>${escapeHtml(fill(L.replaces, { product: preview.replaced.title, price: formatMoney(preview.replaced.price, this.data.moneyFormat) }))}</span>`);
      }
      parts.push(`<span>${escapeHtml(fill(L.routineTotal, { total: formatMoney(preview.totalCents, this.data.moneyFormat) }))}</span>`);
      this.dialog.querySelector('[data-dialog-compare]').innerHTML = preview.alreadyChosen ? '' : parts.join('');

      const choose = this.dialog.querySelector('[data-action="choose-from-panel"]');
      choose.textContent = preview.alreadyChosen ? L.panelChosen : L.chooseForStep;
      choose.disabled = preview.alreadyChosen || product.available === false;
      this.dialog.querySelector('[data-dialog-full]').href = product.url;

      // Size choices, when the product is one of the step's sizes.
      const sizes = sizeOptions(data, id, (sizeId) => this.product(sizeId), this.units());
      const sizeHost = this.dialog.querySelector('[data-dialog-sizes]');
      sizeHost.innerHTML = sizes.length
        ? `<span class="lrb-dialog__sizes-label" id="${this.id}-panel-size">${escapeHtml(L.size)}</span><span class="lrb-sizes" role="group" aria-labelledby="${this.id}-panel-size">${sizes
            .map(
              (size) =>
                `<button type="button" class="lrb-chip" data-action="panel-size" data-id="${Number(size.id)}" aria-pressed="${size.id === id}"${size.available ? '' : ' disabled'}>${escapeHtml(size.label)}</button>`,
            )
            .join('')}</span>`
        : '';
    }

    async #loadPanel(product) {
      const body = this.dialog.querySelector('[data-dialog-body]');
      let html = this.panelCache.get(product.id);
      if (!html) {
        try {
          // No "Accept: application/json" header: with it Shopify answers with
          // the product's own JSON instead of the requested sections.
          const response = await fetch(`${product.url}?sections=liore-routine-product,liore-product-details`);
          if (!response.ok) throw new Error(String(response.status));
          const sections = await response.json();
          if (!sections['liore-routine-product']) throw new Error('missing section');
          html = `${sections['liore-routine-product']}<div class="lrb-dialog__details">${sections['liore-product-details'] || ''}</div>`;
          this.panelCache.set(product.id, html);
        } catch {
          if (this.panelState?.id === product.id) {
            body.innerHTML = `<p class="lrb-dialog__status" role="status">${escapeHtml(this.labels.panelError)}</p>`;
          }
          return;
        }
      }
      // The visitor may have closed the panel or opened another product meanwhile.
      if (!this.dialog.open || this.panelState?.id !== product.id) return;
      body.innerHTML = html;
      this.#preparePanelBody(body);
    }

    /**
     * Turns the product page's details tabs into stacked parts with their own
     * headings (no tabs in the builder), renders the rating with the builder's
     * labels, and keeps the picture dots in step with the gallery.
     */
    #preparePanelBody(body) {
      const L = this.labels;
      const title = body.querySelector('[data-product-title]');
      if (title) {
        title.id = this.dialog.getAttribute('aria-labelledby');
        const product = this.panelState && this.product(this.panelState.id);
        title.insertAdjacentHTML('afterend', this.#strongTagHtml(product));
      }

      const details = body.querySelector('.lrb-dialog__details');
      details?.querySelectorAll('[data-liore-product-details-tab]').forEach((tab) => {
        const part = details.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`);
        if (!part) return;
        const heading = document.createElement('p');
        heading.className = 'lrb-dialog__part-title';
        heading.textContent = tab.textContent.trim();
        part.before(heading);
        part.hidden = false;
        part.removeAttribute('role');
        part.removeAttribute('aria-labelledby');
      });
      details?.querySelector('[role="tablist"]')?.remove();

      // How to use reads before the ingredient list in the panel.
      const partFor = (key) => details?.querySelector(`[id$="-panel-${key}"]`);
      const howTo = partFor('how-to-use');
      const ingredients = partFor('ingredients');
      if (howTo && ingredients) {
        const ingredientsTitle = ingredients.previousElementSibling;
        ingredientsTitle.before(howTo.previousElementSibling, howTo);
      }

      const rating = body.querySelector('[data-product-rating]');
      if (rating) {
        const count = Number(rating.dataset.count) || 0;
        const value = Number(rating.dataset.rating) || 0;
        const link = `<a href="${escapeHtml(rating.dataset.url)}">${escapeHtml(L.readReviews)}</a>`;
        if (count > 0) {
          const full = Math.round(value);
          rating.innerHTML = `<span class="lrb-product__stars" aria-hidden="true">${'★'.repeat(full)}${'☆'.repeat(Math.max(0, 5 - full))}</span><span>${escapeHtml(fill(L.rating, { rating: value.toFixed(1), count }))}</span>${link}`;
        } else {
          rating.innerHTML = `<span>${escapeHtml(L.noReviews)}</span>${link}`;
        }
      }

      const images = body.querySelector('[data-product-images]');
      const dots = [...body.querySelectorAll('.lrb-product__dot')];
      if (images && dots.length) {
        images.addEventListener('scroll', () => {
          const index = Math.round(images.scrollLeft / Math.max(1, images.clientWidth));
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        }, { passive: true });
      }
    }

    /** Back to where the visitor was: the chosen card, or the link that opened the panel. */
    #onPanelClose = () => {
      const panel = this.panelState;
      this.panelState = null;
      if (!panel) return;
      // Chosen in the panel: show the step with its new product, as a row choice does.
      if (panel.revealAfter) {
        this.#revealStep(panel.revealAfter, { onlyIfNeeded: true });
        return;
      }
      const target = panel.trigger?.isConnected
          ? panel.trigger
          : this.querySelector(`[data-action="view-product"][data-id="${CSS.escape(String(panel.id))}"]`);
      target?.focus({ preventScroll: false });
    };

    /** Where focus and the open swap row are, so a re-render can put them back. */
    #snapshot() {
      const active = /** @type {HTMLElement | null} */ (document.activeElement);
      const inside = active && this.contains(active) ? active : null;
      const row = this.openStepId ? this.querySelector(`[data-row="${CSS.escape(this.openStepId)}"]`) : null;
      return {
        focus: inside && inside.dataset.action ? { action: inside.dataset.action, step: inside.dataset.step, id: inside.dataset.id, skin: inside.dataset.skin } : null,
        rowStep: this.openStepId,
        scrollLeft: row ? row.scrollLeft : 0,
      };
    }

    #restoreSnapshot(snapshot) {
      if (snapshot.rowStep && snapshot.rowStep === this.openStepId) {
        const row = this.querySelector(`[data-row="${CSS.escape(snapshot.rowStep)}"]`);
        if (row) row.scrollLeft = snapshot.scrollLeft;
      }
      const f = snapshot.focus;
      if (!f) return;
      // Skip hidden duplicates such as the "+" picture on an empty step.
      const match = [...this.querySelectorAll(`:is(button, a)[data-action="${CSS.escape(f.action)}"]:not([aria-hidden="true"])`)].find(
        (el) => el.dataset.step === f.step && el.dataset.id === f.id && el.dataset.skin === f.skin,
      );
      // The clicked control can disappear (e.g. "Add more steps" chips); fall
      // back to the step it acted on so focus never drops to the page top.
      let fallback = f.step ? this.querySelector(`[data-step-row="${CSS.escape(f.step)}"] [data-action]:not([aria-hidden="true"])`) : null;
      // Picking a skin type swaps the tiles for the banner: land on its button.
      if (!fallback && f.action === 'skin') fallback = this.querySelector('[data-action="change-skin"]');
      (match || fallback)?.focus({ preventScroll: true });
    }

    money(cents) {
      return escapeHtml(formatMoney(cents, this.data.moneyFormat));
    }

    units() {
      return { piece: this.labels.piece, pieces: this.labels.pieces };
    }

    skin() {
      return this.data.skins.find((skin) => skin.handle === this.state.skin) || null;
    }

    stepData(id) {
      return this.data.steps.find((step) => step.id === id);
    }

    /** Freshest description first: page data, then products loaded this visit, then saved picks. */
    product(id) {
      if (id == null) return null;
      return this.data.products[id] || this.data.extra?.[id] || this.state.picked[id] || null;
    }

    /** Page data plus everything loaded this visit, for summarize(). */
    allProducts() {
      return { products: { ...this.data.extra, ...this.data.products } };
    }

    /* ---------------- events ---------------- */

    #onClick = (event) => {
      const target = /** @type {HTMLElement} */ (event.target).closest('[data-action]');
      if (!target || !this.contains(target)) return;
      const { action, step, id, skin } = target.dataset;

      switch (action) {
        case 'reset':
          this.openStepId = null;
          this.#set(createState(this.data, 'starter', this.state.skin));
          break;
        case 'blank':
          this.openStepId = null;
          this.#set(createState(this.data, 'blank', this.state.skin));
          break;
        case 'skin':
          this.showSkinPicker = false;
          this.#set(setSkin(this.state, skin || null));
          this.#track('routine_skin_selected', { skin });
          break;
        case 'change-skin':
          this.showSkinPicker = !this.showSkinPicker;
          this.render();
          break;
        case 'toggle-swap':
          this.openStepId = this.openStepId === step ? null : step;
          if (this.openStepId) this.#ensureLoaded(this.openStepId);
          this.render();
          break;
        case 'choose': {
          // A choice closes the row: the step shows the result, and "Swap"
          // reopens it. The step is brought into view and briefly highlighted.
          const product = this.product(Number(id));
          if (!product) break;
          if (this.openStepId === step) this.openStepId = null;
          this.#set(chooseProduct(this.state, step, product));
          this.#revealStep(step, { onlyIfNeeded: true });
          this.#track('routine_product_swapped', { step, product: handleOf(product) });
          break;
        }
        case 'size': {
          const product = this.product(Number(id));
          if (product) this.#set(chooseProduct(this.state, step, product));
          break;
        }
        case 'skip':
          if (this.openStepId === step) this.openStepId = null;
          this.#set(toggleSkip(this.state, step));
          if (this.state.steps.find((s) => s.id === step)?.skipped) this.#track('routine_step_skipped', { step });
          break;
        case 'add-step':
          this.openStepId = step;
          this.#ensureLoaded(step);
          this.#set(addStep(this.state, step));
          this.#revealStep(step);
          this.#track('routine_step_added', { step });
          break;
        case 'remove-step':
          if (this.openStepId === step) this.openStepId = null;
          this.#set(removeStep(this.state, step, this.data));
          break;
        case 'scroll': {
          const row = this.querySelector(`[data-row="${CSS.escape(step)}"]`);
          const cards = row ? row.querySelectorAll('.lrb-card') : [];
          if (!row || !cards.length) break;
          const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
          const left = nextScrollLeft({
            current: row.scrollLeft,
            max: row.scrollWidth - row.clientWidth,
            step: cards[0].getBoundingClientRect().width + gap,
            gap,
            view: row.clientWidth,
            direction: target.dataset.direction === 'back' ? 'back' : 'forward',
          });
          const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          row.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
          break;
        }
        case 'view-product': {
          // Let new-tab and modified clicks open the product page as usual.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
          event.preventDefault();
          this.#openPanel(step, Number(id), target);
          this.#track('routine_product_viewed', { step, product: handleOf(this.product(Number(id))) });
          break;
        }
        case 'panel-size': {
          // Show that size in the panel: its price, pictures and details.
          const product = this.product(Number(id));
          if (!this.panelState || !product) break;
          this.panelState.id = product.id;
          this.#fillPanelFrame();
          this.dialog.querySelector(`[data-action="panel-size"][data-id="${Number(id)}"]`)?.focus();
          const body = this.dialog.querySelector('[data-dialog-body]');
          if (!this.panelCache.has(product.id)) {
            body.innerHTML = `<p class="lrb-dialog__status" role="status">${escapeHtml(this.labels.panelLoading)}</p>`;
          }
          this.#loadPanel(product);
          break;
        }
        case 'close-product':
          this.dialog?.close();
          break;
        case 'choose-from-panel': {
          const panel = this.panelState;
          const product = panel && this.product(panel.id);
          if (!product) break;
          panel.revealAfter = panel.stepId;
          if (this.openStepId === panel.stepId) this.openStepId = null;
          this.#set(chooseProduct(addStep(this.state, panel.stepId), panel.stepId, product));
          this.dialog?.close();
          this.#track('routine_product_swapped', { step: panel.stepId, product: handleOf(product), from_panel: true });
          break;
        }
        case 'share':
          this.#share(target);
          this.#track('routine_shared');
          break;
        case 'go-to-step':
          this.#revealStep(step);
          break;
        case 'retry': {
          const entry = this.loaded.get(step);
          if (entry) entry.failed = false;
          this.#ensureLoaded(step);
          if (this.openStepId === step) this.#renderPanel(step);
          break;
        }
        case 'add-to-cart':
          this.#addToCart();
          break;
        default:
      }
    };

    /* ---------------- loading a step's products ---------------- */

    async #ensureLoaded(stepId) {
      const step = this.stepData(stepId);
      if (!step) return;
      if (!step.collection?.url) {
        // Hand-picked list: already in the page data, sold-out products left out.
        const ids = (step.choiceIds || []).filter((id) => this.product(id)?.available !== false);
        this.loaded.set(stepId, { ids, page: 1, done: true, loading: false });
        return;
      }
      const entry = this.loaded.get(stepId) || { ids: [], page: 0, done: false, loading: false };
      this.loaded.set(stepId, entry);
      if (entry.loading || entry.done || entry.failed) return;

      entry.loading = true;
      try {
        const url = `${step.collection.url}?section_id=${encodeURIComponent(this.data.swapSectionId)}&page=${entry.page + 1}`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const json = doc.querySelector('script[data-routine-page]')?.textContent || '{"products":[]}';
        const page = JSON.parse(json);
        for (const product of page.products) {
          this.data.extra[product.id] = product;
          entry.ids.push(product.id);
        }
        entry.page += 1;
        entry.done = !page.hasNext;
        entry.failed = false;
      } catch {
        // Leave the step loadable: the visitor can retry from the row.
        entry.failed = true;
      } finally {
        entry.loading = false;
      }
      if (this.openStepId === stepId) {
        const snapshot = this.#snapshot();
        this.#renderPanel(stepId);
        this.#restoreSnapshot(snapshot);
      }
    }

    #watchRowEnd(stepId) {
      this.observer?.disconnect();
      const row = this.querySelector(`[data-row="${CSS.escape(stepId)}"]`);
      const end = row?.querySelector('[data-row-end]');
      const entry = this.loaded.get(stepId);
      // After a failure only the "Try again" button retries; observing here
      // would refetch in a loop because a new observer always fires once.
      if (!row || !end || typeof IntersectionObserver === 'undefined' || entry?.failed || entry?.done) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.#ensureLoaded(stepId);
        },
        { root: row, rootMargin: '0px 400px 0px 0px' },
      );
      this.observer.observe(end);
    }

    /* ---------------- cart ---------------- */

    /** Feature statistics (assets/liore-track.js); a no-op if it isn't loaded. */
    #track(name, params = {}) {
      globalThis.lioreTrack?.(name, params);
    }

    async #addToCart() {
      const summary = summarize(this.state, this.allProducts());
      const { variantIds } = summary;
      const buttons = this.querySelectorAll('[data-action="add-to-cart"]');
      const status = this.querySelector('[data-cart-status]');
      const ok = this.querySelector('[data-cart-ok]');
      const error = this.querySelector('[data-cart-error]');
      if (variantIds.length === 0) return;

      buttons.forEach((button) => {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      });
      if (error) error.hidden = true;
      if (ok) ok.hidden = true;
      if (status) status.textContent = '';

      // Same cart-update contract as snippets/liore-add-all.liquid.
      const routes = (typeof Theme !== 'undefined' && Theme?.routes) || {};
      const addUrl = routes.cart_add_url || '/cart/add.js';
      const post = (items) =>
        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
      const titles = new Map(summary.lines.filter((l) => l.product).map((l) => [l.product.variantId, l.product.title]));
      try {
        const items = cartItems(variantIds, this.fromShare ? 'routine-shared' : 'routine-builder');
        let results;
        const response = await post(items);
        if (response.ok) {
          results = items.map((item) => ({ ok: true, quantity: item.quantity, title: titles.get(item.id) || '' }));
        } else if (response.status === 422) {
          // Shopify refuses the whole batch if one product can't be added
          // (e.g. all stock already in the cart). Add them one by one so the
          // rest still goes in, and keep Shopify's reason for the others.
          results = [];
          for (const item of items) {
            const single = await post([item]);
            let reason = '';
            if (!single.ok) reason = (await single.json().catch(() => ({}))).description || '';
            results.push({ ok: single.ok, quantity: item.quantity, title: titles.get(item.id) || '', reason });
          }
        } else {
          throw new Error(String(response.status));
        }

        const report = cartReport(results, { added: this.labels.added, addedOne: this.labels.addedOne, notAdded: this.labels.notAdded });
        if (status) status.textContent = report.status;
        if (ok) ok.hidden = !report.added;
        if (error && report.error) {
          error.textContent = report.error;
          error.hidden = false;
        }
        if (!report.added) return;
        this.#track('routine_add_to_cart', {
          item_count: results.filter((r) => r.ok).reduce((sum, r) => sum + r.quantity, 0),
          value: summary.totalCents / 100,
          currency: globalThis.Shopify?.currency?.active || 'EUR',
          shared: Boolean(this.fromShare),
        });

        const cartUrl = routes.cart_url || '/cart';
        const update = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
        update.action = 'add';
        update.context = 'product';
        update.promise = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => ({
            cart: { ...cart, totalQuantity: cart.item_count },
            detail: { itemCount: cart.item_count, source: 'liore-routine-builder' },
          }));
        document.dispatchEvent(update);
      } catch {
        if (error) {
          error.textContent = this.labels.cartError;
          error.hidden = false;
        }
      } finally {
        buttons.forEach((button) => {
          button.removeAttribute('aria-busy');
          button.disabled = summarize(this.state, this.allProducts()).count === 0;
        });
      }
    }

    /* ---------------- rendering ---------------- */

    render() {
      const snapshot = this.#snapshot();
      this.#renderSkin();
      this.#renderSteps();
      this.#renderAddable();
      this.#renderSummary();
      if (this.openStepId) this.#renderPanel(this.openStepId);
      this.#restoreSnapshot(snapshot);
    }

    #renderSkin() {
      const host = this.querySelector('[data-skin]');
      if (!host) return;
      const L = this.labels;
      const skin = this.skin();

      const tiles = this.data.skins
        .map(
          (s) => `
          <li><button type="button" class="lrb-skin-tile" data-action="skin" data-skin="${escapeHtml(s.handle)}" aria-pressed="${s.handle === this.state.skin}">
            ${s.image ? `<img src="${escapeHtml(s.image)}" alt="" width="240" height="240" loading="lazy">` : ''}
            <span class="lrb-skin-tile__name">${escapeHtml(s.title)}</span>
            ${s.description ? `<span class="lrb-skin-tile__desc">${escapeHtml(s.description)}</span>` : ''}
          </button></li>`,
        )
        .join('');
      // Opens in a new tab, so the routine in progress stays on screen.
      const guide = this.data.guideUrl
        ? `<a class="lrb-link" href="${escapeHtml(this.data.guideUrl)}" target="_blank" rel="noopener">${escapeHtml(L.notSure)}</a>`
        : '';

      if (!skin || this.showSkinPicker) {
        host.innerHTML = `
          <div class="lrb-skin-question" role="group" aria-labelledby="${this.id}-skin-q">
            <div class="lrb-skin-question__head">
              <span class="lrb-eyebrow">${escapeHtml(skin ? L.skinBuiltFor : L.skinStart)}</span>
              <h2 id="${this.id}-skin-q" class="lrb-skin-question__title">${escapeHtml(L.skinQuestion)}</h2>
              ${guide}
              ${skin ? `<button type="button" class="lrb-button lrb-button--quiet lrb-button--small" data-action="change-skin">${escapeHtml(L.close)}</button>` : ''}
            </div>
            <ul class="lrb-skin-tiles" role="list">${tiles}</ul>
          </div>`;
        return;
      }

      host.innerHTML = `
        <div class="lrb-skin-banner">
          ${skin.image ? `<img class="lrb-skin-banner__img" src="${escapeHtml(skin.image)}" alt="" width="144" height="144">` : ''}
          <div class="lrb-skin-banner__text">
            <span class="lrb-eyebrow">${escapeHtml(L.skinBuiltFor)}</span>
            <span class="lrb-skin-banner__name">${escapeHtml(skin.title)}</span>
            <span class="lrb-skin-banner__note">${escapeHtml(fill(L.skinFirstNote, { skin: skin.title }))}</span>
          </div>
          <div class="lrb-skin-banner__actions">
            <button type="button" class="lrb-button lrb-button--outline" data-action="change-skin" aria-expanded="false">${escapeHtml(L.changeSkin)}</button>
            ${guide}
          </div>
        </div>`;
    }

    #priceHtml(product) {
      if (!product) return '';
      const L = this.labels;
      if (product.compareAt > product.price) {
        return `<span class="lrb-price">
          <span class="visually-hidden">${escapeHtml(L.priceRegular)}</span><s class="lrb-price__was">${this.money(product.compareAt)}</s>
          <span class="visually-hidden">${escapeHtml(L.priceSale)}</span><span class="lrb-price__sale">${this.money(product.price)}</span>
        </span>`;
      }
      return `<span class="lrb-price">${this.money(product.price)}</span>`;
    }

    /** @param {any} product @param {boolean} [compact] Just "-15%", for the small step pictures. */
    /**
     * "Strong active" tag for products tagged strong-active (retinoids,
     * exfoliating acids). Focusable, so the "read How to use" hint shows on
     * tap and keyboard focus as well as on hover.
     */
    #strongTagHtml(product) {
      if (!product?.strong || !this.labels.strongTag) return '';
      const hint = this.labels.strongHint;
      return `<span class="lrb-strong-tag"${hint ? ' tabindex="0"' : ''}>${escapeHtml(this.labels.strongTag)}${
        hint ? `<span class="lrb-strong-tag__hint" role="tooltip">${escapeHtml(hint)}</span>` : ''
      }</span>`;
    }

    #badgeHtml(product, compact = false) {
      if (!product || !(product.compareAt > product.price)) return '';
      const percent = Math.floor(((product.compareAt - product.price) * 100) / product.compareAt);
      const sale = escapeHtml(this.labels.sale);
      let text = percent > 0 ? `${sale}&nbsp;-${percent}%` : sale;
      if (compact && percent > 0) text = `-${percent}%`;
      return `<div class="product-badges product-badges--top-right lrb-badge"><div class="product-badges__badge product-badges__badge--rectangle color-custom-badge-sale">${text}</div></div>`;
    }

    #renderSteps() {
      const list = this.querySelector('[data-steps]');
      if (!list) return;
      const L = this.labels;
      let number = 0;

      list.innerHTML = this.state.steps
        .filter((step) => step.inRoutine)
        .map((step) => {
          number += 1;
          const data = this.stepData(step.id);
          const product = this.product(step.productId);
          const open = this.openStepId === step.id;
          const sizes = sizeOptions(data, step.productId, (id) => this.product(id), this.units());
          const label = escapeHtml(data.name);
          const swapLabel = product ? L.swap : L.choose;

          // Picture and name of a chosen product open its alternatives, like
          // "Swap" (mouse and touch; the button stays the keyboard control).
          // "View product" under the name opens the product panel.
          const pick = !step.skipped && canChoose(data)
            ? ` lrb-step__pick" data-action="toggle-swap" data-step="${escapeHtml(step.id)}`
            : '';
          const image = product?.image
            ? `<span class="lrb-step__image${pick}"><img src="${escapeHtml(product.image)}" alt="" width="200" height="200" loading="lazy">${this.#badgeHtml(product, true)}</span>`
            : !step.skipped && canChoose(data)
              ? // Same action as the "Choose a product" button, which stays the
                // one control for keyboard and screen-reader users.
                `<button type="button" class="lrb-step__image lrb-step__image--empty lrb-step__image--pick" data-action="toggle-swap" data-step="${escapeHtml(step.id)}" tabindex="-1" aria-hidden="true">+</button>`
              : `<span class="lrb-step__image lrb-step__image--empty" aria-hidden="true">+</span>`;

          const soldOut = !step.skipped && product && product.available === false;
          const name = step.skipped
            ? `<span class="lrb-step__name lrb-step__name--skipped">${escapeHtml(product?.title || '')}</span><span class="lrb-step__note">${escapeHtml(L.skippedNote)}</span>`
            : product
              ? `<span class="lrb-step__name${pick}">${escapeHtml(product.title)}</span>${this.#strongTagHtml(product)}${soldOut ? `<span class="lrb-step__note lrb-step__note--sold-out">${escapeHtml(L.soldOut)}</span>` : ''}`
              : `<span class="lrb-step__name lrb-step__name--empty">${escapeHtml(data.hint || L.emptyStep)}</span>`;

          const detailsLink =
            product && !step.skipped
              ? `<a class="lrb-step__details" href="${escapeHtml(product.url)}" data-action="view-product" data-step="${escapeHtml(step.id)}" data-id="${Number(product.id)}">${escapeHtml(L.viewProduct)}<span class="visually-hidden">: ${escapeHtml(product.title)}</span></a>`
              : '';

          const sizeButtons =
            !step.skipped && sizes.length
              ? `<div class="lrb-sizes" role="group" aria-label="${escapeHtml(L.size)}">${sizes
                  .map(
                    (size) =>
                      `<button type="button" class="lrb-chip" data-action="size" data-step="${escapeHtml(step.id)}" data-id="${Number(size.id)}" aria-pressed="${size.id === step.productId}"${size.available ? '' : ' disabled'}>${escapeHtml(size.label)}</button>`,
                  )
                  .join('')}</div>`
              : '';

          const actions = `
            ${step.skipped || !canChoose(data) ? '' : `<button type="button" class="lrb-button ${open ? '' : 'lrb-button--outline'} lrb-button--small" data-action="toggle-swap" data-step="${escapeHtml(step.id)}" aria-expanded="${open}" aria-controls="${this.id}-panel-${escapeHtml(step.id)}">${escapeHtml(open ? L.close : swapLabel)}</button>`}
            <button type="button" class="lrb-button lrb-button--quiet lrb-button--small" data-action="skip" data-step="${escapeHtml(step.id)}" aria-pressed="${step.skipped}">${escapeHtml(step.skipped ? L.addBack : L.skip)}</button>
            ${data.starter ? '' : `<button type="button" class="lrb-button lrb-button--quiet lrb-button--small" data-action="remove-step" data-step="${escapeHtml(step.id)}">${escapeHtml(L.remove)}</button>`}`;

          return `
            <li class="lrb-step${step.skipped ? ' lrb-step--skipped' : ''}" data-step-row="${escapeHtml(step.id)}">
              <div class="lrb-step__main">
                <span class="lrb-step__number" aria-hidden="true">${String(number).padStart(2, '0')}</span>
                ${image}
                <div class="lrb-step__body">
                  <span class="lrb-step__meta"><span class="lrb-step__label"><span class="lrb-step__label-number">${String(number).padStart(2, '0')}</span>${label}</span>${data.when ? `<span class="lrb-when lrb-when--${escapeHtml(data.whenKey)}">${escapeHtml(data.when)}</span>` : ''}</span>
                  ${name}
                  ${sizeButtons}
                  ${detailsLink}
                </div>
                <div class="lrb-step__side">
                  ${step.skipped || !product ? '' : this.#priceHtml(product)}
                  <div class="lrb-step__actions">${actions}</div>
                </div>
              </div>
              <div class="lrb-panel" id="${this.id}-panel-${escapeHtml(step.id)}" ${open ? '' : 'hidden'}></div>
            </li>`;
        })
        .join('');
    }


    #renderPanel(stepId) {
      const panel = this.querySelector(`#${CSS.escape(`${this.id}-panel-${stepId}`)}`);
      if (!panel) return;
      const L = this.labels;
      const data = this.stepData(stepId);
      const step = this.state.steps.find((s) => s.id === stepId);
      const skin = this.skin();
      const skinIds = (skin && skin.steps?.[stepId]) || [];
      const entry = this.loaded.get(stepId) || { ids: [], done: false, loading: true };
      const ids = orderAlternatives({ currentId: step.productId, skinIds, loadedIds: entry.ids });
      const skinSet = new Set(skinIds);

      const cards = ids
        .map((id) => this.product(id))
        .filter(Boolean)
        .map((product) => {
          const chosen = product.id === step.productId;
          const forSkin = skin && skinSet.has(product.id);
          // Picture and name choose the product too (mouse and touch); the
          // "Choose" button stays the one control for keyboard users.
          return `
            <li class="lrb-card${chosen ? ' lrb-card--chosen' : ''}">
              <span class="lrb-card__image lrb-card__pick" data-action="choose" data-step="${escapeHtml(stepId)}" data-id="${Number(product.id)}">${product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="200" height="200" loading="lazy">` : ''}${this.#badgeHtml(product)}</span>
              ${forSkin || product.strong
                ? `<span class="lrb-card__tags">${forSkin ? `<span class="lrb-card__tag">${escapeHtml(fill(L.forSkin, { skin: skin.title }))}</span>` : ''}${this.#strongTagHtml(product)}</span>`
                : ''}
              <span class="lrb-card__name lrb-card__pick" data-action="choose" data-step="${escapeHtml(stepId)}" data-id="${Number(product.id)}">${escapeHtml(product.title)}</span>
              <span class="lrb-card__foot">
                ${this.#priceHtml(product)}
                <button type="button" class="lrb-button lrb-button--small ${chosen ? 'lrb-button--chosen' : 'lrb-button--outline'}" data-action="choose" data-step="${escapeHtml(stepId)}" data-id="${Number(product.id)}" aria-pressed="${chosen}">${escapeHtml(chosen ? L.chosen : L.chooseProduct)}<span class="visually-hidden">: ${escapeHtml(product.title)}</span></button>
              </span>
              <a class="lrb-card__details" href="${escapeHtml(product.url)}" data-action="view-product" data-step="${escapeHtml(stepId)}" data-id="${Number(product.id)}">${escapeHtml(L.viewProduct)}<span class="visually-hidden">: ${escapeHtml(product.title)}</span></a>
            </li>`;
        })
        .join('');

      const count = data.collection?.count;
      // "All Creams" for a collection; a hand-picked list is just the step name.
      const heading = data.collection ? fill(L.allInStep, { count: count ?? '', collection: data.collection.title }) : data.name;
      const status = entry.failed ? L.loadError : entry.loading && cards === '' ? L.loading : '';
      const retry = entry.failed
        ? ` <button type="button" class="lrb-button lrb-button--quiet lrb-button--small" data-action="retry" data-step="${escapeHtml(stepId)}">${escapeHtml(L.retry)}</button>`
        : '';

      // The row's last item is the "load more" trigger with its spinner; it is
      // left out once everything is loaded, so it never makes a row look
      // wider than its products (which would show arrows for nothing).
      panel.innerHTML = `
        <div class="lrb-panel__head">
          <span class="lrb-panel__title">
            <span class="lrb-panel__heading">${escapeHtml(heading)}</span>
            ${skin && skinIds.length ? `<span class="lrb-panel__sub">${escapeHtml(fill(L.skinFirst, { skin: skin.title }))}</span>` : ''}
          </span>
          <span class="lrb-panel__nav">
            <button type="button" class="lrb-arrow" data-action="scroll" data-step="${escapeHtml(stepId)}" data-direction="back" aria-label="${escapeHtml(L.scrollBack)}">‹</button>
            <button type="button" class="lrb-arrow" data-action="scroll" data-step="${escapeHtml(stepId)}" data-direction="forward" aria-label="${escapeHtml(L.scrollForward)}">›</button>
          </span>
        </div>
        <ul class="lrb-row" role="list" data-row="${escapeHtml(stepId)}" tabindex="0" aria-label="${escapeHtml(heading)}">
          ${cards}
          ${entry.done || entry.failed ? '' : '<li class="lrb-row__end" data-row-end aria-hidden="true"><span class="lrb-row__spinner"></span></li>'}
        </ul>
        ${status ? `<p class="lrb-panel__status" role="status">${escapeHtml(status)}${retry}</p>` : ''}`;

      this.#watchRowEnd(stepId);
      this.#syncArrows(stepId);
    }

    /**
     * The ‹ › arrows only appear when the row has more than fits; at the
     * start or end the arrow that cannot move is disabled. Re-checked on
     * scroll, when more products arrive and when the row changes size.
     */
    #syncArrows(stepId) {
      const panel = this.querySelector(`#${CSS.escape(`${this.id}-panel-${stepId}`)}`);
      const row = panel?.querySelector('[data-row]');
      const nav = panel?.querySelector('.lrb-panel__nav');
      if (!row || !nav) return;
      const update = () => {
        const overflow = row.scrollWidth - row.clientWidth > 1;
        nav.hidden = !overflow;
        const [back, forward] = nav.querySelectorAll('[data-action="scroll"]');
        // A few pixels of slack: card snapping can stop just short of the edge.
        if (back) back.disabled = row.scrollLeft <= 4;
        if (forward) forward.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
      };
      update();
      row.addEventListener('scroll', update, { passive: true });
      this.arrowObserver?.disconnect();
      if (typeof ResizeObserver !== 'undefined') {
        this.arrowObserver = new ResizeObserver(update);
        this.arrowObserver.observe(row);
      }
      // Pictures load after the first measurement and can change the width.
      row.querySelectorAll('img').forEach((img) => img.complete || img.addEventListener('load', update, { once: true }));
    }

    #renderAddable() {
      const host = this.querySelector('[data-addable]');
      if (!host) return;
      const buttons = this.data.steps
        .filter((step) => !step.starter)
        // A step needs something to offer: a suggested product or a collection to swap from.
        .filter((step) => canChoose(step) || step.productId != null)
        .filter((step) => !this.state.steps.find((s) => s.id === step.id)?.inRoutine)
        .map(
          (step) =>
            `<li><button type="button" class="lrb-chip lrb-chip--add" data-action="add-step" data-step="${escapeHtml(step.id)}">+ ${escapeHtml(step.name)}${step.when ? ` <span class="lrb-chip__when">${escapeHtml(step.when)}</span>` : ''}</button></li>`,
        )
        .join('');
      host.innerHTML = buttons;
      host.closest('[data-addable-box]')?.toggleAttribute('hidden', buttons === '');
    }

    #renderSummary() {
      const L = this.labels;
      const summary = summarize(this.state, this.allProducts());
      const skin = this.skin();

      const list = this.querySelector('[data-summary-lines]');
      if (list) {
        let number = 0;
        list.innerHTML = summary.lines
          .map((line) => {
            number += 1;
            const name = escapeHtml(this.stepData(line.stepId).name);
            const prefix = String(number).padStart(2, '0');
            // Each line jumps to its step in the routine.
            const go = (label, price, muted) =>
              `<li${muted ? ' class="lrb-summary__muted"' : ''}><button type="button" class="lrb-summary__step" data-action="go-to-step" data-step="${escapeHtml(line.stepId)}"><span>${label}</span><span>${price}</span></button></li>`;
            if (line.status === 'chosen') return go(`${prefix} ${name}`, this.money(line.priceCents), false);
            const notes = { skipped: L.summarySkipped, empty: L.summaryEmpty, unavailable: L.soldOut };
            return go(`${prefix} ${name} · ${escapeHtml(notes[line.status])}`, '–', true);
          })
          .join('');
      }

      const title = this.querySelector('[data-summary-title]');
      if (title) title.textContent = skin ? fill(L.summaryFor, { skin: skin.title }) : L.summaryTitle;

      const countText = summary.count === 1 ? L.oneProduct : fill(L.products, { count: summary.count });
      this.querySelectorAll('[data-summary-count]').forEach((el) => (el.textContent = countText));
      this.querySelectorAll('[data-summary-total]').forEach((el) => (el.innerHTML = this.money(summary.totalCents)));
      this.querySelectorAll('[data-action="add-to-cart"]').forEach((button) => {
        if (!button.hasAttribute('aria-busy')) button.disabled = summary.count === 0;
      });
      const empty = this.querySelector('[data-summary-empty]');
      if (empty) empty.hidden = summary.lines.some((line) => line.status === 'chosen');
    }
  }

  customElements.define('liore-routine-builder', LioreRoutineBuilder);
}
